import {
  RACIALS,
  RUNES,
  SPELLS,
  type RacialId,
  type RuneId,
  type SlotKey,
  type SpellId,
} from '@arena/shared';
import { createContext, useContext, useState } from 'react';
import { assetMayExist, assetUrl } from '../../app/assetUrl';

/** HUD slot groups (spec §3): the frame colour of a spell slot and its shop column header. */
export type SlotGroup = 'offense' | 'mobility' | 'control';

export const SLOT_GROUPS: Record<SlotKey, SlotGroup> = {
  Q: 'offense',
  W: 'offense',
  E: 'offense',
  R: 'offense',
  D: 'mobility',
  F: 'mobility',
  C: 'control',
  V: 'control',
};

/** What each slot is for, shown next to its input in the shop ("RMB · Mobility"). */
export const SLOT_ROLES: Record<SlotKey, string> = {
  Q: 'Basic',
  W: 'Projectile',
  E: 'Instant',
  R: 'Power',
  D: 'Mobility',
  F: 'Defense',
  C: 'Utility',
  V: 'Control',
};

/** Faint glyph an empty slot shows, so new players learn the slot structure. */
export const SLOT_GROUP_GLYPH: Record<SlotGroup, string> = {
  offense: '✦',
  mobility: '➶',
  control: '◎',
};

/** Glyph a racial icon shows until its art lands. */
const RACIAL_GLYPH = '✧';

/** Public URL of a spell icon (128 px webp). */
export const spellIconUrl = (id: SpellId) => `/icons/spells/${id}.webp`;
/** Public URL of a rune icon (128 px webp): the spell's silhouette plus the rune motif. */
export const runeIconUrl = (id: RuneId) => `/icons/runes/${id}.webp`;
/** Public URL of a racial icon (128 px webp). */
export const racialIconUrl = (id: RacialId) => `/icons/racials/${id}.webp`;

/**
 * Whether HUD icons and badges carry a native `title` hover tooltip. The shop turns them off
 * (DROW-174): everything it knows is printed on the page, and nothing pops up on hover.
 */
export const HoverTitles = createContext(true);

/** URLs that failed once this page load: every later icon goes straight to the placeholder. */
const missing = new Set<string>();

/**
 * An icon that shows its art when it exists, and until then (or if it fails to load) the slot
 * group's glyph with the name as its label. Dropping the webp files into `public/icons/` is the
 * whole swap-in: no code changes. Builds skip the request for art they don't ship.
 */
function ArtIcon({ url, glyph, label }: { url: string; glyph: string; label: string }) {
  const [, setBroken] = useState(false);
  const title = useContext(HoverTitles) ? label : undefined;
  if (missing.has(url) || !assetMayExist(url)) {
    return (
      <span className="icon icon-placeholder" role="img" aria-label={label} title={title}>
        {glyph}
      </span>
    );
  }
  return (
    <img
      className="icon"
      src={assetUrl(url)}
      alt={label}
      title={title}
      draggable={false}
      onError={() => {
        missing.add(url);
        setBroken(true);
      }}
    />
  );
}

const spellGlyph = (id: SpellId) => SLOT_GROUP_GLYPH[SLOT_GROUPS[SPELLS[id].slot]];

export function SpellIcon({ id }: { id: SpellId }) {
  return <ArtIcon url={spellIconUrl(id)} glyph={spellGlyph(id)} label={SPELLS[id].name} />;
}

export function RuneIcon({ id }: { id: RuneId }) {
  const def = RUNES[id];
  return <ArtIcon url={runeIconUrl(id)} glyph={spellGlyph(def.spellId)} label={def.name} />;
}

export function RacialIcon({ id }: { id: RacialId }) {
  return <ArtIcon url={racialIconUrl(id)} glyph={RACIAL_GLYPH} label={RACIALS[id].name} />;
}

/** Test hook: forget which icons failed to load. */
export function resetMissingIcons(): void {
  missing.clear();
}
